import { useState } from 'react';
import { useSetRecoilState } from 'recoil';
import { useTasksTranslation } from '~/backend/connector-next-api/connector-next-components';
import useProcessDicomFiles from '~/hooks/useProcessDicomFiles';
import { useFuseGeneratedImage } from '~/hooks/workflows/useFuseGeneratedImage';
import { Job } from '~/state/atoms/jobAtoms';
import { workflowModalState, WorkflowTypes } from '~/state/atoms/viewerAtoms';
import { PredictionModel } from '~/utils/transforms/predictionModelsDataTransforms';
import { transformTaskToGenerationModel } from '~/utils/transforms/taskTransforms';
import { NewJobData, useJobManagement } from '../../useJobManagement';
import useCheckTaskInputs from '../useCheckTaskInputs';
import { handleTaskStartError, requireTaskId } from '../utils';

type UseImageTranslationTaskProps = {
    patientID: string;
    studyId: string;
    seriesId: string;
};

/**
 * A hook for creating an image translation task via Connector API.
 * @param {UseImageTranslationTaskProps} props - Contains study and series identifiers for the task.
 * @returns {Object} - An object containing the start function and submission state.
 */
export default function useImageTranslationTask({
    patientID,
    studyId,
    seriesId,
}: UseImageTranslationTaskProps) {
    const setWorkflowModalState = useSetRecoilState(workflowModalState);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { mutateAsync: startTranslation } = useTasksTranslation();
    const checkTaskInputs = useCheckTaskInputs();
    const { addJob, updateJob } = useJobManagement();
    const { processCreatedFiles } = useProcessDicomFiles();
    const fuseGeneratedImage = useFuseGeneratedImage();

    const handleProcessedFiles = async (job: Job) => {
        if (job.results && job.results?.length > 0) {
            const generationModel = transformTaskToGenerationModel(job.task);
            const processedItems = await processCreatedFiles(
                job.results,
                generationModel
            );
            if (processedItems) {
                if (processedItems.structureSets.length > 0) {
                    updateJob(job.id, { isImported: true });
                }
                fuseGeneratedImage(processedItems);
            }
        }
    };

    /**
     * Creates the image translation task with the provided model.
     * @param {PredictionModel} selectedModel - The model to use for the image translation task.
     */
    const start = async (
        selectedModel: PredictionModel,
        customizationName?: string
    ) => {
        if (isSubmitting) {
            return;
        }

        setIsSubmitting(true);
        try {
            await checkTaskInputs({
                patientId: patientID,
                seriesInstanceUids: [seriesId],
            });

            const modelIdentifier = customizationName
                ? `${selectedModel.name}.${customizationName}`
                : selectedModel.name;

            const task = await startTranslation({
                body: {
                    modelName: modelIdentifier,
                    imageSeries: {
                        patientID,
                        seriesInstanceUID: seriesId,
                        studyInstanceUID: studyId,
                    },
                },
            });

            const newJob: NewJobData = {
                name: `Image+`,
                id: requireTaskId(task),
                type: WorkflowTypes.IMAGE,
                task: {
                    model: selectedModel,
                    output: task,
                },
                onLoad: handleProcessedFiles,
            };

            addJob(newJob);
            setWorkflowModalState((state) => ({
                ...state,
                open: false,
            }));
        } catch (error) {
            handleTaskStartError(
                error,
                'Failed to start image translation task.'
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    return { start, isSubmitting };
}
